<template lang="pug">
div
    Chart(:option="chartOption" style="height:60vh; max-height:520px")

    .ctl-bar
        a-button(type="primary" @click="getData") 刷新
        a-button(type="primary" @click="handleAdd" style="margin-left:10px") 添加/更新 PnL

    a-modal(v-model="formVisible"
        :title="form._id ? '更新 PnL' : '添加 PnL'"
        @ok="submitForm"
        ok-text="保存"
        cancel-text="取消")
        a-form-model(:model="form" :label-col="{ span: 5 }" :wrapper-col="{ span: 18 }")
            a-form-model-item(label="日期")
                a-date-picker(v-model="form.date"
                    format="YYYY-MM-DD"
                    style="width:100%"
                    :disabled="!!form._id")
            a-form-model-item(label="AUM")
                a-input-number(v-model="form.aum" :min="0" :step="0.01" style="width:100%")
            a-form-model-item(label="PnL")
                a-input-number(v-model="form.pnl" :step="0.01" style="width:100%")

    h3(style="margin-top:20px;margin-bottom:10px") PnL 明细
    a-tabs(tab-position="left" v-model="activeUserKey")
        a-tab-pane(v-for="u in userTabs" :key="u.key" :tab="u.label")
            a-table(:columns="u.isMe ? columns : readonlyColumns"
                :dataSource="u.records"
                size="small"
                rowKey="date"
                :pagination="{ pageSize: 50 }")
                template(slot="action" slot-scope="record" v-if="u.isMe")
                    a(@click="handleEdit(record)") 编辑
</template>

<script>
import _ from 'lodash';
import moment from 'moment';
import Chart from '../../components/Chart.vue';

const BENCHMARK_LABEL = '中证1000';

export default {
    props: ['product', 'myStrategyName', 'meId', 'indexCloses'],
    components: { Chart },
    data() {
        return {
            loading: false,
            records: [],
            activeUserKey: '',
            formVisible: false,
            form: { _id: '', date: null, aum: 0, pnl: 0 },
            columns: [
                { title: '日期', dataIndex: 'date' },
                { title: 'AUM', dataIndex: 'aum' },
                { title: 'PnL', dataIndex: 'pnl' },
                { title: 'PnL%', dataIndex: 'pnlPct', customRender: (t) => t == null ? '-' : t.toFixed(2) + '%' },
                { title: `Alpha vs ${BENCHMARK_LABEL}`, dataIndex: 'alpha', customRender: (t) => t == null ? '-' : t.toFixed(2) + '%' },
                { title: '操作', width: 100, scopedSlots: { customRender: 'action' } },
            ],
            readonlyColumns: [
                { title: '日期', dataIndex: 'date' },
                { title: 'AUM', dataIndex: 'aum' },
                { title: 'PnL', dataIndex: 'pnl' },
                { title: 'PnL%', dataIndex: 'pnlPct', customRender: (t) => t == null ? '-' : t.toFixed(2) + '%' },
                { title: `Alpha vs ${BENCHMARK_LABEL}`, dataIndex: 'alpha', customRender: (t) => t == null ? '-' : t.toFixed(2) + '%' },
            ],
        };
    },
    computed: {
        userTabs() {
            const allDates = _.uniq(this.records.map(r => r.date)).sort();
            const benchDailyMap = this.buildBenchmarkDailyMap();

            const byUser = _.groupBy(this.records, r => r.userId ? r.userId._id : 'unknown');
            const tabs = _.map(byUser, (recs, uid) => {
                const first = recs[0];
                const isMe = uid === this.meId;
                const marker = (first.strategyName && first.strategyName.trim()) || first.userName || uid;
                const sorted = _.sortBy(recs, 'date');
                const rows = sorted.map(r => {
                    const pnlPct = r.aum ? r.pnl / r.aum * 100 : 0;
                    const alpha = pnlPct - (benchDailyMap[r.date] || 0);
                    return {
                        _id: r._id,
                        date: r.date,
                        aum: r.aum,
                        pnl: r.pnl,
                        pnlPct: Number(pnlPct.toFixed(4)),
                        alpha: Number(alpha.toFixed(4)),
                    };
                });
                return {
                    key: uid,
                    label: isMe ? `${marker}（我）` : marker,
                    isMe,
                    records: rows,
                };
            });
            return _.sortBy(tabs, t => (t.isMe ? 0 : 1));
        },
        chartOption() {
            if (!this.records.length) return { title: { text: '暂无数据', x: 'center', y: 'center', textStyle: { fontWeight: 'normal' } } };

            const byUser = _.groupBy(this.records, r => r.userId ? r.userId._id : 'unknown');
            const userIds = Object.keys(byUser);

            const userSeriesMap = {};
            const userMarkers = {};
            userIds.forEach(uid => {
                const recs = _.sortBy(byUser[uid], 'date');
                let cum = 0;
                const map = {};
                recs.forEach(r => {
                    const dailyPct = r.aum ? r.pnl / r.aum : 0;
                    cum += dailyPct;
                    map[r.date] = cum * 100;
                });
                userSeriesMap[uid] = map;
                const firstRec = recs[0];
                const marker = (firstRec.strategyName && firstRec.strategyName.trim()) || firstRec.userName || uid;
                userMarkers[uid] = marker;
            });

            const allDates = _.uniq(this.records.map(r => r.date)).sort();

            const totalMap = {};
            const totalDailyMap = {};
            let totalCum = 0;
            allDates.forEach(d => {
                const dayRecs = this.records.filter(r => r.date === d);
                const totalPnl = _.sumBy(dayRecs, 'pnl');
                const totalAum = _.sumBy(dayRecs, 'aum');
                const dailyPct = totalAum > 0 ? totalPnl / totalAum * 100 : 0;
                totalDailyMap[d] = dailyPct;
                if (totalAum > 0) totalCum += totalPnl / totalAum;
                totalMap[d] = totalCum * 100;
            });

            const benchMap = this.buildBenchmarkMap(allDates);

            const alphaMap = {};
            allDates.forEach(d => {
                alphaMap[d] = totalMap[d] - (benchMap[d] || 0);
            });

            const series = userIds.map(uid => ({
                name: userMarkers[uid],
                type: 'line',
                showSymbol: false,
                data: allDates.map(d => {
                    const v = userSeriesMap[uid][d];
                    return v != null ? Number(v.toFixed(4)) : null;
                }),
            }));
            series.push({
                name: 'Total',
                type: 'line',
                showSymbol: false,
                lineStyle: { width: 3 },
                data: allDates.map(d => Number((totalMap[d] || 0).toFixed(4))),
            });
            series.push({
                name: `Alpha vs ${BENCHMARK_LABEL}`,
                type: 'line',
                showSymbol: false,
                lineStyle: { width: 3, type: 'dashed' },
                data: allDates.map(d => Number(alphaMap[d].toFixed(4))),
            });
            series.push({
                name: 'Daily PnL%',
                type: 'bar',
                yAxisIndex: 1,
                data: allDates.map(d => Number((totalDailyMap[d] || 0).toFixed(4))),
            });

            return {
                legend: {},
                tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, valueFormatter: v => (v == null ? '-' : v.toFixed(2) + '%') },
                dataZoom: [{ type: 'inside', zoomOnMouseWheel: true, moveOnMouseMove: true, moveOnMouseWheel: false }],
                xAxis: { type: 'category', data: allDates },
                yAxis: [
                    { type: 'value', scale: true, axisLabel: { formatter: '{value}%' } },
                    { type: 'value', scale: true, axisLabel: { formatter: '{value}%' }, name: 'Daily PnL%' },
                ],
                series,
            };
        },
    },
    mounted() {
        this.getData();
    },
    methods: {
        getData() {
            this.loading = true;
            this.$api.request('GET', `productPnl/${encodeURIComponent(this.product)}`)
                .then(res => {
                    this.records = res.data;
                    const meTab = this.userTabs.find(t => t.isMe);
                    this.activeUserKey = meTab ? meTab.key : (this.userTabs[0] ? this.userTabs[0].key : '');
                    this.loading = false;
                })
                .catch(() => { this.loading = false; });
        },
        buildBenchmarkMap(allDates) {
            if (!this.indexCloses || !this.indexCloses.length || !allDates.length) return {};
            const sorted = _.sortBy(this.indexCloses, 'date');
            const firstPnLDate = allDates[0];

            let firstClose = null;
            for (const c of sorted) {
                if (c.date <= firstPnLDate) firstClose = c.close;
                else break;
            }
            if (firstClose == null) firstClose = sorted[0].close;

            const benchMap = {};
            let lastClose = null;
            let idx = 0;
            allDates.forEach(d => {
                while (idx < sorted.length && sorted[idx].date <= d) {
                    lastClose = sorted[idx].close;
                    idx++;
                }
                if (lastClose != null) benchMap[d] = (lastClose / firstClose - 1) * 100;
            });
            return benchMap;
        },
        buildBenchmarkDailyMap() {
            if (!this.indexCloses || !this.indexCloses.length) return {};
            const sorted = _.sortBy(this.indexCloses, 'date');
            const map = {};
            for (let i = 1; i < sorted.length; i++) {
                const prev = sorted[i - 1].close;
                const cur = sorted[i].close;
                if (prev) map[sorted[i].date] = (cur / prev - 1) * 100;
            }
            return map;
        },
        handleAdd() {
            this.form = { _id: '', date: null, aum: 0, pnl: 0 };
            this.formVisible = true;
        },
        handleEdit(record) {
            this.form = {
                _id: record._id,
                date: moment(record.date),
                aum: record.aum,
                pnl: record.pnl,
            };
            this.formVisible = true;
        },
        submitForm() {
            if (!this.form.date) {
                this.$message.error('日期不能为空');
                return;
            }
            if (this.form.aum == null || this.form.aum < 0) {
                this.$message.error('AUM 不能为空');
                return;
            }
            const dateStr = moment(this.form.date).format('YYYY-MM-DD');
            this.$api.request('POST', 'productPnl', {
                product: this.product,
                date: dateStr,
                aum: this.form.aum,
                pnl: this.form.pnl || 0,
            })
            .then(() => {
                this.$message.success('保存成功');
                this.formVisible = false;
                this.getData();
            });
        },
    },
};
</script>

<style lang="less" scoped>
.ctl-bar {
    margin: 15px 0;
}
</style>
